import {
  horizontalEllipsis,
  nbsp,
} from '@ipfabric/frontend-shared-assets/utils/characters';
import {
  type Point,
  type ReportSeverity,
  type TableColumn,
  type TableColumnFormat,
  type TableColumnWidths,
  getBaseColumnField,
  isBackendColumnFormat,
  safeAbsurd,
} from '@ipfabric/utils';
import classNames from 'classnames';
import type { RenderedTableCell } from 'components/Table/cells/RenderedTableCell';
import { renderCellContentByType } from 'components/Table/cells/renderCellContentByType';
import { getCellWidthAndPaddingStyle } from 'components/Table/helpers/getCellWidthAndPaddingStyle';
import { getColumnDefaultWidth } from 'components/Table/helpers/getColumnWidth';
import { hasColumnLoadedDependencies } from 'components/Table/helpers/hasColumnLoadedDependencies';
import type { TableCellContext } from 'components/Table/model/TableCellContext';
import type { TableRowMeta } from 'components/Table/model/TableRowMeta';
import { pipe } from 'fp-ts/lib/function';
import type { JSX, MouseEvent, ReactNode } from 'react';

const loadingCell: RenderedTableCell = {
  content: horizontalEllipsis,
};

const columnHasNotAvailableIntentRule = (
  baseField: string | null,
  showOnlyIntentResultsOnColumns?: string[],
) =>
  baseField !== null &&
  showOnlyIntentResultsOnColumns !== undefined &&
  !showOnlyIntentResultsOnColumns.includes(baseField);

const getCellSeverity = (
  row: unknown | undefined,
  format: TableColumnFormat<any>,
  showOnlyIntentResultsOnColumns?: string[],
) => {
  const baseField = isBackendColumnFormat(format)
    ? getBaseColumnField(format)
    : null;
  if (
    baseField === null ||
    columnHasNotAvailableIntentRule(baseField, showOnlyIntentResultsOnColumns)
  ) {
    return null;
  }
  const severity = (row as TableRowMeta)?.$severity?.[baseField];
  switch (severity) {
    case 0:
    case 10:
    case 20:
    case 30:
      return severity;
    case -1:
    case undefined:
      return null;
    default:
      safeAbsurd(severity);
      return null;
  }
};

const getSeverityClass = (
  content: ReactNode,
  severity: ReportSeverity | null,
) =>
  severity === null
    ? null
    : classNames(
        'ipf-table__severity-badge',
        `ipf-table__severity-badge--severity-${severity}`,
        (content === null || content === '') &&
          'ipf-table__severity-badge--empty',
      );

export const makeRenderCell =
  (
    row: unknown | undefined,
    renderedColumnWidths: TableColumnWidths | undefined,
    context: TableCellContext,
    index: number,
    openContextMenu: ((point: Point) => void) | null,
    showOnlyIntentResultsOnColumns?: string[],
  ) =>
  (column: TableColumn<any>): JSX.Element => {
    const severity = getCellSeverity(
      row,
      column.format,
      showOnlyIntentResultsOnColumns,
    );
    const renderedCell = hasColumnLoadedDependencies(column, row)
      ? renderCellContentByType(column, { row, context, index, severity })
      : loadingCell;

    return (
      <div
        className={classNames(
          'ipf-table__cell',
          renderedCell.className,
          renderedCell.alignment === 'right' && 'text-right',
        )}
        data-column={column.key}
        key={column.key}
        style={pipe(
          renderedColumnWidths?.[column.key] ?? getColumnDefaultWidth(column),
          getCellWidthAndPaddingStyle(renderedCell.paddingX),
        )}
        onContextMenu={(e: MouseEvent) => {
          if (openContextMenu === null) {
            return;
          }
          // Invoke standard context menu for subcomponents
          // so user is still able to open links in new tab etc
          if (e.currentTarget === e.target) {
            e.preventDefault();

            openContextMenu({
              x: e.clientX,
              y: e.clientY,
            });
          }
        }}
      >
        <div
          className={classNames(
            'ipf-table__cell-content',
            getSeverityClass(renderedCell.content, severity),
          )}
        >
          {/* nbsp fallback to keep inline formatting for empty value  */}
          {renderedCell.content ?? nbsp}
        </div>
      </div>
    );
  };
